import React from "react";
import { Target, Users, Clock, CheckCircle, Mail, Trophy } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { Card, CardContent } from "../components/ui/card";

const FootballXAcademy = () => {
  const skills = [
    {
      title: "1v1 Skills",
      description: "Master individual defending and attacking situations with advanced techniques and decision making.",
      icon: <Target className="w-8 h-8 text-cyan-400" />
    },
    {
      title: "First Touch",
      description: "Develop exceptional ball control and first touch under pressure in various game situations.",
      icon: <Users className="w-8 h-8 text-cyan-400" />
    },
    {
      title: "Scanning & Awareness",
      description: "Improve game intelligence through enhanced scanning techniques and spatial awareness.",
      icon: <Clock className="w-8 h-8 text-cyan-400" />
    },
    {
      title: "Passing Accuracy",
      description: "Perfect short and long-range passing techniques with emphasis on timing and precision.",
      icon: <CheckCircle className="w-8 h-8 text-cyan-400" />
    },
    {
      title: "Clinical Finishing",
      description: "Enhance shooting technique and composure in front of goal with various finishing methods.",
      icon: <Target className="w-8 h-8 text-cyan-400" />
    },
    {
      title: "Ball Mastery",
      description: "Develop close ball control, dribbling, and manipulation skills for tight spaces.",
      icon: <Users className="w-8 h-8 text-cyan-400" />
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video className="w-full h-full object-cover" autoPlay muted loop playsInline>
            <source src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/7unn7a5x_logo-reveal-intro-maker-featuring-neon-lights-and-a-tunnel-effect-637-el1.MP4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              FOOTBALL X <span className="text-cyan-400">ACADEMY</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Focused development of core football skills through specialized training sessions. 
              Our academy program targets individual improvement in technical abilities that form 
              the foundation of exceptional football performance.
            </p>
          </div>
        </div>
      </section>

      {/* EOI - Full Time Team */}
      <section className="py-20 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              2027 FULL TIME & <span className="text-cyan-400">PART TIME</span> PROGRAMS
            </h2>
            <p className="text-lg text-gray-400 italic mt-2">
              Develop. Compete. <span className="text-cyan-400 font-semibold">Excel.</span>
            </p>
            <div className="mt-4 inline-block bg-cyan-500 text-black font-bold px-6 py-2 rounded-full text-lg">
              AGES: U7's TO U13's
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 max-w-5xl mx-auto">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-cyan-400 transition-colors">
              <Users className="w-10 h-10 text-cyan-400 mb-4" />
              <h4 className="text-lg font-bold mb-2">Elite Player Development</h4>
              <p className="text-gray-400 text-sm">High quality training focused on skill, confidence and long term player development.</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-cyan-400 transition-colors">
              <Trophy className="w-10 h-10 text-cyan-400 mb-4" />
              <h4 className="text-lg font-bold mb-2">Professional Coaching</h4>
              <p className="text-gray-400 text-sm">Experienced, qualified coaches committed to bringing out the best in every player.</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-cyan-400 transition-colors">
              <Target className="w-10 h-10 text-cyan-400 mb-4" />
              <h4 className="text-lg font-bold mb-2">Competitive Pathway</h4>
              <p className="text-gray-400 text-sm">Clear pathway with opportunities to grow, compete and reach your full potential.</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-cyan-400 transition-colors">
              <CheckCircle className="w-10 h-10 text-cyan-400 mb-4" />
              <h4 className="text-lg font-bold mb-2">Character & Leadership</h4>
              <p className="text-gray-400 text-sm">Building strong individuals on and off the field through values and teamwork.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
            <div className="bg-gray-900 border border-cyan-400/30 rounded-xl p-6 text-center">
              <Clock className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
              <h4 className="text-xl font-bold mb-1">2 TO 3 NIGHTS</h4>
              <p className="text-cyan-400 font-semibold text-sm mb-2">A WEEK TRAINING</p>
              <p className="text-gray-400 text-sm">Consistent training. Real development. Better players.</p>
            </div>
            <div className="bg-gray-900 border border-cyan-400/30 rounded-xl p-6 text-center">
              <Trophy className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
              <h4 className="text-xl font-bold mb-1">COMPETITION</h4>
              <p className="text-cyan-400 font-semibold text-sm mb-2">SPL 1, 2 AND 3</p>
              <p className="text-gray-400 text-sm">Compete at the highest level in the SPL league system.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto mb-12">
            {[
              { icon: <Users className="w-6 h-6" />, title: "Full Time Program", desc: "Comprehensive training, education and player development." },
              { icon: <Clock className="w-6 h-6" />, title: "Part Time Program", desc: "Flexible schedule with high quality training." },
              { icon: <Users className="w-6 h-6" />, title: "Ages U7's to U13's", desc: "Age appropriate training for every stage." },
              { icon: <Trophy className="w-6 h-6" />, title: "Competition SPL", desc: "Compete at the highest level in SPL 1, 2 and 3." },
              { icon: <CheckCircle className="w-6 h-6" />, title: "More Than A Team", desc: "It's a community. It's a culture. It's Football X." },
            ].map((item, i) => (
              <div key={i} className="text-center p-3">
                <div className="text-cyan-400 flex justify-center mb-2">{item.icon}</div>
                <h5 className="text-sm font-bold mb-1">{item.title}</h5>
                <p className="text-gray-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-cyan-500 rounded-2xl p-8 text-center max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold text-black mb-2">2027 EXPRESSION OF INTEREST</h3>
            <p className="text-xl font-bold text-black mb-1">NOW OPEN</p>
            <p className="text-black/70 mb-6">Spaces are limited — Register Today</p>
            <a
              href="https://form.jotform.com/261577826480063"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-black hover:bg-gray-800 text-white font-bold rounded-lg text-lg transition-colors duration-200"
              data-testid="eoi-fulltime-cta"
            >
              Register Your Interest
            </a>
          </div>
        </div>
      </section>

      {/* Skills Development */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              CORE SKILLS <span className="text-cyan-400">DEVELOPMENT</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Our comprehensive curriculum focuses on the fundamental skills that separate good players from great ones. 
              Each session is designed to challenge and improve specific aspects of your game.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {skills.map((skill, index) => (
              <Card key={index} className="bg-gray-900 border-gray-800 hover:border-cyan-400 transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="mb-4">{skill.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{skill.title}</h3>
                  <p className="text-gray-400">{skill.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Programs */}
      <section className="py-20 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              SPECIALIZED <span className="text-cyan-400">PROGRAMS</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Take your game to the next level with our elite specialized programs designed for players who demand more.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
            {/* X High Performance */}
            <div className="group relative rounded-2xl overflow-hidden border border-gray-800 hover:border-red-500 transition-all duration-300 bg-gradient-to-b from-gray-900 to-black" data-testid="x-high-performance-card">
              <div className="p-8 flex flex-col items-center text-center">
                <div className="mb-6 w-48 h-48 flex items-center justify-center">
                  <img src="https://customer-assets.emergentagent.com/job_4d83666a-aa6c-4973-8e91-b3c87719f098/artifacts/rkpfvp15_X%20High.PNG" alt="X High Performance" className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(239,68,68,0.4)]" />
                </div>
                <h3 className="text-2xl font-bold mb-3">X High Performance</h3>
                <p className="text-gray-400 leading-relaxed">
                  Our High Performance Program is designed to improve players Physical Performance and Reduce Injury risk:
                </p>
                <ul className="text-gray-400 mt-4 space-y-2 text-left">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-red-500 shrink-0" /> Speed, Agility and Acceleration</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-red-500 shrink-0" /> Strength Development (age-appropriate)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-red-500 shrink-0" /> Balance, Coordination & Mobility</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-red-500 shrink-0" /> Injury Prevention & Movement Mechanics</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-red-500 shrink-0" /> Conditioning for Match Performance</li>
                </ul>
              </div>
            </div>

            {/* X Goal Keepers */}
            <div className="group relative rounded-2xl overflow-hidden border border-gray-800 hover:border-green-500 transition-all duration-300 bg-gradient-to-b from-gray-900 to-black" data-testid="x-goalkeepers-card">
              <div className="p-8 flex flex-col items-center text-center">
                <div className="mb-6 w-48 h-48 flex items-center justify-center">
                  <img src="https://customer-assets.emergentagent.com/job_4d83666a-aa6c-4973-8e91-b3c87719f098/artifacts/1q2238hn_X%20GK.PNG" alt="X Goal Keepers" className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(74,222,128,0.4)]" />
                </div>
                <h3 className="text-2xl font-bold mb-3">X Goal Keepers</h3>
                <p className="text-gray-400 leading-relaxed">
                  A dedicated goalkeeper development program focusing on:
                </p>
                <ul className="text-gray-400 mt-4 space-y-2 text-left">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Shot-Stopping</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Distribution (Hands and Feet)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Positioning & Decision Making</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Communication</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> 1V1 Situations</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Game Based Scenarios</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Philosophy */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">
                OUR TRAINING <span className="text-cyan-400">PHILOSOPHY</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Individual Focus, Team Success</h3>
                <p className="text-gray-400 mb-6">
                  We believe that exceptional teams are built from exceptional individuals. Our academy 
                  focuses on developing each player's technical abilities while understanding how these 
                  skills contribute to team success.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-cyan-400 mt-1" />
                    <div>
                      <h4 className="font-semibold">Progressive Skill Development</h4>
                      <p className="text-gray-400">Structured progression from basic to advanced techniques</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-cyan-400 mt-1" />
                    <div>
                      <h4 className="font-semibold">Game-Realistic Training</h4>
                      <p className="text-gray-400">All drills designed to replicate match situations</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-cyan-400 mt-1" />
                    <div>
                      <h4 className="font-semibold">Individual Attention</h4>
                      <p className="text-gray-400">Personalized feedback and development plans</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-cyan-400 mb-6">Training Focus</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-gray-700 pb-2">
                      <span>Technical Skills Development</span>
                      <span className="text-cyan-400">Primary</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-700 pb-2">
                      <span>1v1 Specialized Training</span>
                      <span className="text-cyan-400">Core</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-700 pb-2">
                      <span>Small-Sided Games</span>
                      <span className="text-cyan-400">Applied</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Individual Assessment</span>
                      <span className="text-cyan-400">Ongoing</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-cyan-500">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-black mb-6">
              CONTACT FOOTBALL X ACADEMY
            </h2>
            <p className="text-xl text-black/80 mb-8">
              Ready to start your football journey with us? Get in touch to learn more about our academy programs and training sessions.
            </p>
            <div className="bg-black rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Get In Touch</h3>
              <p className="text-lg text-gray-300 mb-6">Contact Football X Academy</p>
              <div className="flex items-center justify-center gap-3">
                <Mail className="w-6 h-6 text-cyan-400" />
                <a href="mailto:academy@football-x.com.au" className="text-xl font-semibold hover:text-cyan-400 transition-colors duration-200">
                  academy@football-x.com.au
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FootballXAcademy;
